import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/project/karuna/components/ui/card"; // @ts-ignore - card is not a valid module
import { Button } from "@/project/karuna/components/ui/button"; // @ts-ignore - button is not a valid module
import {
  BookOpen,
  Users,
  Award,
  Heart,
  Target,
  Globe,
  Sparkles,
  TreePine,
} from "lucide-react";
import { Link } from "react-router-dom"; // @ts-ignore - react-router-dom is not a valid module
import PublicLayout from "./PublicLayout";
import navigationRoutes, {
  useKarunaLink,
} from "@/project/karuna/utils/navigationRoutes";

const Programs = () => {
  const getLink = useKarunaLink();
  return (
    <PublicLayout>
      {/* Hero Section */}
      <section className="w-full bg-gradient-hero py-20 text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="mb-6 text-5xl font-bold">Our Programs</h1>
          <p className="mx-auto max-w-3xl text-xl">
            Comprehensive value-based education initiatives designed to nurture
            compassionate global citizens
          </p>
        </div>
      </section>

      {/* Main Programs */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-4xl font-bold">Core Programs</h2>
            <div className="mx-auto h-1 w-24 bg-primary" />
          </div>
          <div className="mx-auto grid max-w-6xl gap-8 md:grid-cols-2">
            <Card className="shadow-medium transition-shadow hover:shadow-strong">
              <CardHeader>
                <BookOpen className="mb-4 h-12 w-12 text-primary" />
                <CardTitle className="text-2xl">Karuna Clubs</CardTitle>
                <CardDescription>
                  School-based compassion initiatives
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="mb-4 text-muted-foreground">
                  Establish student-led clubs focused on promoting kindness,
                  compassion, and ethical behavior. Activities include awareness
                  campaigns, community service, and animal welfare projects.
                </p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Weekly compassion activities</li>
                  <li>• Student leadership development</li>
                  <li>• Community engagement projects</li>
                  <li>• Annual awards and recognition</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="shadow-medium transition-shadow hover:shadow-strong">
              <CardHeader>
                <Users className="mb-4 h-12 w-12 text-secondary" />
                <CardTitle className="text-2xl">Value Education</CardTitle>
                <CardDescription>
                  Curriculum integration programs
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="mb-4 text-muted-foreground">
                  Comprehensive value education modules designed to integrate
                  compassion, ethics, and environmental awareness into existing
                  school curricula across all grade levels.
                </p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Age-appropriate lesson plans</li>
                  <li>• Teacher training workshops</li>
                  <li>• Interactive learning materials</li>
                  <li>• Assessment and evaluation tools</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="shadow-medium transition-shadow hover:shadow-strong">
              <CardHeader>
                <TreePine className="mb-4 h-12 w-12 text-accent" />
                <CardTitle className="text-2xl">
                  Environmental Conservation
                </CardTitle>
                <CardDescription>Eco-awareness initiatives</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="mb-4 text-muted-foreground">
                  Programs focused on environmental stewardship, wildlife
                  conservation, and sustainable living practices to instill a
                  sense of responsibility towards our planet.
                </p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Tree plantation drives</li>
                  <li>• Wildlife protection awareness</li>
                  <li>• Waste management projects</li>
                  <li>• Sustainable living workshops</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="shadow-medium transition-shadow hover:shadow-strong">
              <CardHeader>
                <Heart className="mb-4 h-12 w-12 text-primary" />
                <CardTitle className="text-2xl">Animal Welfare</CardTitle>
                <CardDescription>Compassion for all beings</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="mb-4 text-muted-foreground">
                  Educational programs promoting kindness and ethical treatment
                  of animals, including workshops on preventing cruelty and
                  fostering empathy towards all living creatures.
                </p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Animal welfare workshops</li>
                  <li>• Cruelty prevention campaigns</li>
                  <li>• Pet care education</li>
                  <li>• Veterinary care support</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Activity Types */}
      <section className="bg-muted/30 py-20">
        <div className="container mx-auto px-4">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-4xl font-bold">Activity Categories</h2>
            <div className="mx-auto mb-6 h-1 w-24 bg-primary" />
            <p className="mx-auto max-w-3xl text-xl text-muted-foreground">
              Diverse activities designed to engage students at all levels
            </p>
          </div>
          <div className="mx-auto grid max-w-5xl gap-6 md:grid-cols-3">
            <Card>
              <CardHeader>
                <Target className="mb-3 h-10 w-10 text-primary" />
                <CardTitle>Intra School Activities</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Within-school competitions, events, and programs that foster
                  compassion among students.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Award className="mb-3 h-10 w-10 text-secondary" />
                <CardTitle>Inter School Events</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Collaborative activities bringing together multiple schools
                  for larger impact initiatives.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Sparkles className="mb-3 h-10 w-10 text-accent" />
                <CardTitle>National Programs</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Large-scale campaigns and events celebrating compassion at a
                  national level.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Globe className="mb-3 h-10 w-10 text-primary" />
                <CardTitle>Community Outreach</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Programs extending beyond schools to impact wider communities
                  and society.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <BookOpen className="mb-3 h-10 w-10 text-secondary" />
                <CardTitle>Workshops & Training</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Professional development for educators and capacity building
                  for student leaders.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Users className="mb-3 h-10 w-10 text-accent" />
                <CardTitle>Public Relations</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Media engagement and awareness campaigns promoting our mission
                  and values.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-accent py-20 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="mb-6 text-4xl font-bold">Start Your Journey</h2>
          <p className="mx-auto mb-8 max-w-2xl text-xl">
            Register your school to participate in our life-changing programs
            and activities
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button asChild>
              <Link to={getLink(navigationRoutes.DEMO_LOGIN)}>
                School Registration
              </Link>
            </Button>
            <Button className="bg-white/10 hover:bg-white/20" asChild>
              <Link to={getLink(navigationRoutes.CONTACT)}>
                Get Information
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default Programs;
